/* eslint-disable default-case */
import { useState, useEffect } from "react";
import { useUser } from "../contexts/UserContext";
import { calculatePurchaseFee, FEE_CONFIG } from "../utils/roleUtils";
import { httpsCallable } from "firebase/functions";
import { functions } from "../constants/firebase";
import { ROUTES } from "../App";
import useMoveTo from "../constants/navigation";
import useExchangeRates from "../hooks/useExchangeRates";

const JobCheckout = ({
    job,
    applicant,
    isOpen,
    onClose,
    basePrice, // The creator's set cost
}) => {
    const { currentUser } = useUser();
    const navigate = useMoveTo();
    const { convertPrice } = useExchangeRates();
    const [loading, setLoading] = useState(false);
    const [editableAmount, setEditableAmount] = useState(basePrice);
    const [isInApp, setIsInApp] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [isAndroid, setIsAndroid] = useState(false);

    // Detect if running in React Native WebView
    useEffect(() => {
        if (window.isInApp !== undefined) {
            setIsInApp(window.isInApp);
            setIsIOS(window.isIOS || false);
            setIsAndroid(window.isAndroid || false);
        } else {
            const userAgent = navigator.userAgent || navigator.vendor || window.opera;
            setIsInApp(userAgent.includes('ZylenApp'));
            setIsIOS(/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream);
            setIsAndroid(/android/i.test(userAgent));
        }
    }, []);

    // Listen for payment results from React Native
    useEffect(() => {
        const handleMessage = (event) => {
            try {
                const message = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

                switch (message.type) {
                    case 'STRIPE_PAYMENT_SUCCESS':
                    case 'PAYSTACK_PAYMENT_COMPLETED':
                        window.showToast("Payment successful! 🎉", "success");
                        navigate(`${ROUTES.JOBS_MY_POSTS}?hire=success`, { replace: true });
                        onClose();
                        setLoading(false);
                        break;

                    case 'STRIPE_PAYMENT_CANCELED':
                    case 'PAYSTACK_PAYMENT_CANCELED':
                        window.showToast("Payment cancelled", "info");
                        setLoading(false);
                        break;

                    case 'STRIPE_PAYMENT_ERROR':
                    case 'PAYSTACK_PAYMENT_ERROR':
                        window.showToast(message.error || "Payment failed. Please try again.", "error");
                        setLoading(false);
                        break;

                    case 'STRIPE_PAYMENT_VERIFICATION_FAILED':
                    case 'PAYSTACK_PAYMENT_VERIFICATION_FAILED':
                        window.showToast("Payment verification failed. Please contact support.", "error");
                        setLoading(false);
                        break;
                }
            } catch (e) {
                // Not a JSON message, ignore
            }
        };

        if (isInApp) {
            document.addEventListener('message', handleMessage);  // Android
            window.addEventListener('message', handleMessage);  // iOS
            return () => {
                document.removeEventListener("message", handleMessage);
                window.removeEventListener('message', handleMessage);
            };
        }
    }, [isInApp, navigate, onClose]);
    // ✅ Synchronize editableAmount when basePrice prop changes
    useEffect(() => {
        if (basePrice !== undefined) {
            setEditableAmount(basePrice);
        }
    }, [basePrice]);

    if (!isOpen || !job || !applicant) return null;

    // ✅ Calculate job secure fee based on the APPLICANT'S (Creator's) role
    const { fee: secureFee, total: finalTotal, rate: feeRate } = calculatePurchaseFee(
        currentUser,
        editableAmount,
        'job',
        applicant // Pass applicant data to check their role
    );

    const handlePaystackPayment = async (
        creatorData,
        job,
        buyerData,
        basePrice,
        secureFee,
        totalPrice
    ) => {
        try {
            const initializePaystack = httpsCallable(functions, "initializePaystackPayment");

            // Convert values to ZAR (Jobs are USD based)
            const basePriceZar = convertPrice(basePrice, "USD", "ZAR");
            const secureFeeZar = convertPrice(secureFee, "USD", "ZAR");
            const totalPriceZar = convertPrice(totalPrice, "USD", "ZAR");
            const creatorPayout = Math.max(0, basePrice - secureFee);
            const creatorPayoutZar = Math.max(0, basePriceZar - secureFeeZar);

            const queryParams = new URLSearchParams({
                type: 'job_secure',
                jobId: job.id,
                userId: buyerData.uid,
                creatorId: creatorData.uid,
            }).toString();

            const payload = {
                email: buyerData.email,
                amount: Math.round(totalPriceZar * 100),
                currency: "ZAR",
                subaccount: creatorData.paystackSubaccountCode,
                // Platform bears the Paystack fee so the freelancer receives the
                // full amount minus our commission (per pricing spec).
                bearer: "account",
                callback_url: isInApp
                    ? `https://zylen.app/paystack-redirect.html?${queryParams}`
                    : `${window.location.origin}/paystack-redirect.html?${queryParams}`,
                metadata: {
                    firebaseUID: buyerData.uid,
                    jobId: job.id,
                    creatorId: creatorData.uid,
                    // Persist the payout mapping into the transaction record (auditable
                    // from the sale itself). Backend must write this into
                    // Transactions/salesHistory alongside the Paystack reference.
                    subaccountCode: creatorData.paystackSubaccountCode,
                    originalCurrency: "USD",
                    basePrice: basePrice,
                    secureFee: secureFee,
                    totalPrice: basePrice,
                    creatorPayout,
                    basePriceZar: basePriceZar,
                    secureFeeZar: secureFeeZar,
                    totalPriceZar: basePriceZar,
                    creatorPayoutZar,
                    userRole: buyerData.role || "Free",
                    jobTitle: job.title,
                    type: "job_secure",
                    platform: isInApp ? (isIOS ? 'ios' : 'android') : 'web',
                },
            };

            // ✅ For in-app purchases, include additional metadata
            if (isInApp) {
                payload.metadata.inAppPayment = true;
                payload.metadata.devicePlatform = isIOS ? 'ios' : 'android';
            }

            // Platform commission (15% free / 6% pro) taken off the top; the
            // backend recomputes this authoritatively from the creator's role.
            const applicationFeeAmountZar = Math.round(secureFeeZar * 100);
            if (applicationFeeAmountZar > 0) {
                payload.transaction_charge = applicationFeeAmountZar;
            }

            const result = await initializePaystack(payload);

            if (result.data?.status && result.data?.data?.authorization_url) {
                if (isInApp) {
                    window.ReactNativeWebView.postMessage(JSON.stringify({
                        type: 'PAYSTACK_PAYMENT',
                        authorizationUrl: result.data.data.authorization_url,
                        reference: result.data.data.reference,
                        accessCode: result.data.data.access_code,
                        jobId: job.id,
                    }));
                } else {
                    window.location.href = result.data.data.authorization_url;
                }
            } else {
                console.error("Paystack init error:", result.data);
                const errorMsg = result.data?.message || "Failed to initialize Paystack payment.";
                window.showToast(errorMsg, "error");
            }
        } catch (err) {
            console.error("Paystack init failed:", err);
            window.showToast("Paystack payment error. Try again.", "error");
        } finally {
            if (!isInApp) setLoading(false);
        }
    };

    const handlePurchase = async () => {
        if (!currentUser?.uid) {
            window.showToast("Please login to proceed.", "error");
            return;
        }

        setLoading(true);
        try {
            const isStripeCreator = !!applicant.stripeConnectedAccountId;
            const isPaystackCreator = !!applicant.paystackSubaccountCode;

            if (!isStripeCreator && !isPaystackCreator) {
                throw new Error("This creator hasn't set up their payout account yet.");
            }

            if (isStripeCreator) {
                const createJobCheckout = httpsCallable(functions, "createJobCheckoutSession");

                const checkoutData = {
                    userId: currentUser.uid,
                    email: currentUser.email,
                    jobId: job.id,
                    jobTitle: job.title,
                    creatorId: applicant.uid,
                    basePrice: basePrice,
                    currency: "usd", // Jobs are currently USD based
                    inApp: isInApp,
                    platform: isIOS ? 'ios' : (isAndroid ? 'android' : 'mobile'),
                };

                if (isInApp) {
                    const result = await createJobCheckout(checkoutData);
                    if (result.data.clientSecret) {
                        window.ReactNativeWebView.postMessage(JSON.stringify({
                            type: 'STRIPE_CHECKOUT',
                            clientSecret: result.data.clientSecret,
                            customerId: result.data.customerId,
                            merchantDisplayName: `Hire ${applicant.applicantName}`,
                            totalPrice: finalTotal,
                        }));
                    } else {
                        throw new Error("Failed to initialize secure payment.");
                    }
                } else {
                    const result = await createJobCheckout({
                        ...checkoutData,
                        successUrl: `${window.location.origin}/#/my-jobs?hire=success`,
                        cancelUrl: `${window.location.origin}/#/my-jobs?hire=cancelled`,
                    });

                    if (result.data.url) {
                        window.location.href = result.data.url;
                    } else {
                        throw new Error("Failed to create checkout session.");
                    }
                }
            } else if (isPaystackCreator) {
                await handlePaystackPayment(
                    applicant,
                    job,
                    currentUser,
                    editableAmount,
                    secureFee,
                    finalTotal
                );
            }
        } catch (error) {
            console.error("Job checkout error:", error);
            window.showToast(error.message || "Checkout failed.", "error");
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white dark:bg-[#2A2A2A] rounded-2xl w-full max-w-md shadow-xl">
                <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                    <h2 className="text-2xl font-bold text-black dark:text-white">
                        Secure Service
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Hiring {applicant.applicantName} for "{job.title}"
                    </p>
                </div>

                <div className="px-6 py-4 space-y-4">
                    <div className="space-y-3">
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Service Cost ($)
                            </label>
                            <input
                                type="number"
                                value={editableAmount}
                                onChange={(e) => setEditableAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                                className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-[#1E1E1E] border border-gray-200 dark:border-gray-700 focus:border-[#327691] outline-none font-semibold text-lg"
                                placeholder="0.00"
                            />
                        </div>

                        <div className="flex justify-between text-gray-600 dark:text-gray-400 px-1">
                            <span className="text-sm">Creator Commission ({feeRate * 100}%)</span>
                            <span className="text-sm font-medium">-${secureFee.toFixed(2)}</span>
                        </div>

                        <div className="flex justify-between text-gray-600 dark:text-gray-400 px-1">
                            <span className="text-sm">Creator Receives</span>
                            <span className="text-sm font-medium">${Math.max(0, editableAmount - secureFee).toFixed(2)}</span>
                        </div>

                        <div className="flex justify-between items-center text-xl font-bold text-black dark:text-white pt-3 border-t border-gray-100 dark:border-gray-800 px-1">
                            <span>Total to Pay</span>
                            <span className="text-[#327691]">${finalTotal.toFixed(2)}</span>
                        </div>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                        <p className="text-xs text-blue-800 dark:text-blue-300">
                            🛡️ <strong>Secure Payment:</strong> Funds are held securely and released to the creator once the service is confirmed.
                        </p>
                    </div>
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex gap-3">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="flex-1 px-4 py-3 rounded-xl font-semibold border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handlePurchase}
                        disabled={loading}
                        className="flex-1 px-4 py-3 rounded-xl font-semibold bg-[#327691] text-white hover:bg-[#22516a] transition disabled:opacity-50"
                    >
                        {loading ? "Processing..." : `Pay $${finalTotal.toFixed(2)}`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default JobCheckout;
